import { useState, useCallback } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/auth-context'
import { useMfaSession } from '@/features/auth/hooks/use-mfa-session'
import { MfaChallenge } from '@/features/auth/components/mfa-challenge'
import type { ReactNode } from 'react'

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const { isMfaRequired, factorId, markMfaVerified, loading: mfaLoading } = useMfaSession()
  const [mfaVerified, setMfaVerified] = useState(false)

  const handleMfaSuccess = useCallback(() => {
    markMfaVerified()
    setMfaVerified(true)
  }, [markMfaVerified])

  if (loading || mfaLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-zero-600" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth" replace />
  }

  // Show MFA challenge if session has expired and user has MFA enrolled
  if (isMfaRequired && !mfaVerified && factorId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-gray-900">Session Verification</h1>
            <p className="mt-1 text-sm text-gray-500">Please verify your identity to continue.</p>
          </div>
          <MfaChallenge factorId={factorId} onSuccess={handleMfaSuccess} />
        </div>
      </div>
    )
  }

  return <>{children}</>
}
